import React, { useEffect, useState } from 'react';
import { auditService } from '../../services/auditService';
import { AuditEvent } from '../../types/audit';
import { Link } from 'react-router-dom';

function formatDateInput(date: string) {
  // yyyy-mm-dd
  return date.slice(0, 10);
}

export const AuditPage: React.FC = () => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [user, setUser] = useState('Todos');
  const [action, setAction] = useState('Todas');
  const [document, setDocument] = useState('Todos');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    fetchAuditEvents();
  }, []);

  const fetchAuditEvents = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {
        search: '',
        userName: user !== 'Todos' ? user : undefined,
        actionName: action !== 'Todas' ? action : undefined,
        documentName: document !== 'Todos' ? document : undefined,
        dateStart: dateStart || undefined,
        dateEnd: dateEnd || undefined,
        page,
        pageSize
      };
      
      const result = await auditService.getFilteredAuditEvents(filters);
      setEvents(result.data);
      setTotalEvents(result.totalCount);
      setTotalPages(result.totalPages);
      setCurrentPage(page);
    } catch (err) {
      setError('Erro ao carregar eventos de auditoria. Tente novamente.');
      console.error('Erro ao buscar eventos:', err);
    } finally {
      setLoading(false);
    }
  };

  // Buscar dados quando os filtros mudarem
  useEffect(() => {
    fetchAuditEvents(1);
  }, [user, action, document, dateStart, dateEnd]);

  const users = Array.from(new Set(events.map(e => e.userName)));
  const actions = Array.from(new Set(events.map(e => e.actionName)));
  const documents = Array.from(new Set(events.map(e => e.documentName)));

  const handleRefresh = () => {
    fetchAuditEvents(currentPage);
  };

  // Funções de paginação
  const handleFirstPage = () => {
    fetchAuditEvents(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchAuditEvents(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchAuditEvents(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    fetchAuditEvents(totalPages);
  };

  const handlePageChange = (page: number) => {
    fetchAuditEvents(page);
  };

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Auditoria de Documentos</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erro ao carregar dados</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={handleRefresh}
                  className="bg-red-100 text-red-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Auditoria de Documentos</h1>
        <p className="text-gray-600 mt-1">Acompanhe todas as ações realizadas nos documentos do sistema</p>
      </div>
      
      <form className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="flex flex-col">
          <label className="mb-1 font-medium text-gray-700">Usuário</label>
          <select value={user} onChange={e => setUser(e.target.value)} className="rounded-md border px-3 py-2">
            <option>Todos</option>
            {users.map(u => <option key={u}>{u}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="mb-1 font-medium text-gray-700">Ação</label>
          <select value={action} onChange={e => setAction(e.target.value)} className="rounded-md border px-3 py-2">
            <option>Todas</option>
            {actions.map(a => <option key={a}>{a}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="mb-1 font-medium text-gray-700">Documento</label>
          <select value={document} onChange={e => setDocument(e.target.value)} className="rounded-md border px-3 py-2">
            <option>Todos</option>
            {documents.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="mb-1 font-medium text-gray-700">Data Inicial</label>
          <input
            type="date"
            className="rounded-md border px-3 py-2"
            value={dateStart}
            onChange={e => setDateStart(e.target.value)}
            lang="pt-BR"
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1 font-medium text-gray-700">Data Final</label>
          <input
            type="date"
            className="rounded-md border px-3 py-2"
            value={dateEnd}
            onChange={e => setDateEnd(e.target.value)}
            lang="pt-BR"
          />
        </div>
        </div>
      </form>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Data/Hora</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Usuário</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Ação</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-64">Documento</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">Detalhes</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8">Carregando...</td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8">Nenhum evento encontrado.</td></tr>
            ) : events.map(event => (
              <tr key={event.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm text-gray-900">
                  {new Date(event.createdAt).toLocaleString('pt-BR')}
                </td>
                <td className="px-4 py-4 text-sm text-gray-900">
                  {event.userName}
                </td>
                <td className="px-4 py-4 text-sm text-gray-900">
                  {event.actionName}
                </td>
                <td className="px-4 py-4 text-sm text-gray-900">
                  <div className="max-w-xs truncate" title={event.documentName}>
                    {event.documentName}
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-900">
                  <div className="max-w-xs truncate" title={event.actionDescription}>
                    {event.actionDescription}
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-right">
                  <Link 
                    to={`/audit/${event.id}`} 
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    Ver detalhes
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalEvents)} de {totalEvents} eventos
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Botão Primeira Página */}
            <button
              onClick={handleFirstPage}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Primeira página"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Botão Página Anterior */}
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Página anterior"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Números das Páginas */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber: number;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`px-3 py-1 text-sm font-medium rounded-md ${
                      currentPage === pageNumber
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            
            {/* Botão Próxima Página */}
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Próxima página"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            {/* Botão Última Página */}
            <button
              onClick={handleLastPage}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Última página"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 